"use client";

import { useQuery } from "@tanstack/react-query";
import { useParams } from "next/navigation";
import { EventBookingPageClient } from "@/features/hiives/components/event-booking-hub/event-booking-page";
import { hiivesKeys } from "@/features/hiives/queries/keys";
import { EVENT_BOOKING_LOAD_ERROR_MESSAGE } from "./event-booking-section.shared";
import type { EventBookingSectionData } from "./event-booking-section.types";

type EventBookingSectionClientProps = {
  initialData: EventBookingSectionData;
};

export function EventBookingSectionClient({
  initialData,
}: EventBookingSectionClientProps) {
  const params = useParams<{ hiiveSlug: string; eventSlug: string }>();
  const hiiveSlug = params.hiiveSlug ?? "";
  const eventSlug = params.eventSlug ?? "";
  const hasSlugs = hiiveSlug.length > 0 && eventSlug.length > 0;
  const invalidEventBookingKey = [
    ...hiivesKeys.eventAll(),
    "booking",
    "invalid",
    "invalid",
  ] as const;

  const eventBookingQuery = useQuery<EventBookingSectionData>({
    queryKey: hasSlugs
      ? hiivesKeys.eventBookingPage(hiiveSlug, eventSlug)
      : invalidEventBookingKey,
    queryFn: async () => initialData,
    initialData,
    staleTime: Number.POSITIVE_INFINITY,
    enabled: hasSlugs,
  });

  const eventBooking = eventBookingQuery.data ?? initialData;

  if (!hasSlugs || !eventBooking || eventBookingQuery.error) {
    return (
      <div className="flex h-dvh flex-col items-center justify-center bg-background px-4 text-center">
        <p className="text-sm text-text-secondary">
          {EVENT_BOOKING_LOAD_ERROR_MESSAGE}
        </p>
      </div>
    );
  }

  return (
    <EventBookingPageClient
      hiive={eventBooking.hiive}
      event={eventBooking.event}
      occurrences={eventBooking.occurrences}
      bookingRestriction={eventBooking.bookingRestriction}
      canBookEvent={eventBooking.canBookEvent}
      deferredProfileAnswers={eventBooking.deferredProfileAnswers}
      allowedTierNames={eventBooking.allowedTierNames}
      sessionUser={eventBooking.sessionUser}
      viewerTicketedOccurrenceIds={eventBooking.viewerTicketedOccurrenceIds}
      hiiveSlug={eventBooking.hiiveSlug}
      eventSlug={eventBooking.eventSlug}
      packs={eventBooking.packs}
      viewerPackHoldings={eventBooking.viewerPackHoldings}
    />
  );
}
